/* Shader da água — portado VERBATIM do painel.template.html.
 *
 * Esta matemática levou muitas rodadas de ajuste com o usuário: densidade
 * do fbm, velocidade do domain warp, intensidade do especular, profundidade
 * do azul. Não reescrever. Se precisar mexer, mexa nos uniformes.
 *
 * O arquivo original tinha uma trava de geração em Python (validar_template)
 * porque uma crase perdida dentro deste literal derrubava o script inteiro
 * e a página abria parecendo certa, sem funcionar nada. Aqui o TypeScript
 * pega isso na compilação.
 */

export const VERTEX = `attribute vec2 p; void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;

export const FRAGMENT = `
    precision highp float;
    uniform vec2 uRes;
    uniform float uTime;
    uniform float uMergulho;   // profundidade percorrida pela camera
    uniform float uEscuro;     // 0 = campo aceso, 1 = preto total
    uniform float uClaro;      // 0 = abismo, 1 = superficie iluminada
    // 0 = agua como sempre; 1 = abismo reluzente da tela de varredura.
    // NAO e um tema novo: e a MESMA agua com a luz liberada. A tela de
    // varredura tem um filtro CSS que puxa tudo para preto, entao a luz
    // que sobrava aqui virava um unico foco (a massa de ruido cai sempre
    // do mesmo lado). Este uniforme abre a faixa de luz, solta a vinheta e
    // acelera o fluxo — o brilho passa a percorrer a tela inteira em vez
    // de morar num canto.
    uniform float uFossa;
    // color1 e color2 sao o mesmo navy, entao na pratica e um gradiente de
    // dois tons. Abaixo deles entra um abismo quase preto: e o que da a
    // sensacao de fundo inexplorado, com o navy funcionando como meio-tom e
    // nao como chao.
    const vec3 ABISSO = vec3(0.008, 0.014, 0.078);  // ~#02041f
    const vec3 NAVY   = vec3(0.000, 0.000, 0.498);  // #00007f
    // A luz que atravessa agua continua azul. Usar o quase-branco como cor
    // principal do brilho e o que fazia a tela parecer metal polido.
    const vec3 AGUA   = vec3(0.300, 0.520, 0.800);  // luz submersa
    const vec3 LUZ    = vec3(0.969, 1.000, 0.988);  // #f7fffc, so no topo
    // O filamento do abismo tem cor propria, entre a agua e a luz. Somando
    // LUZ pura ele chegava saturado no filtro e virava tinta branca chapada;
    // puxado para o azul ele continua sendo LUZ ATRAVESSANDO agua, que e o
    // que faz parecer transparente em vez de pintado.
    const vec3 FIO    = vec3(0.620, 0.750, 0.900);
    // O reflexo da home e LAVANDA TRANSLUCIDO, nao branco (2026-09-29): o
    // Pedro mandou um recorte de como queria — "um pouco transparente" — e a
    // cor media do reflexo nele e (133, 140, 213). Somado ao azul de baixo,
    // ele deixa a agua aparecer atraves, em vez de tapar.
    const vec3 REFLEXO = vec3(0.620, 0.660, 1.000);
    // O AZUL DO MERGULHO NO CLARO (19/10): no escuro a descida cai no preto;
    // no claro ela atravessa este azul — o do sistema, um pouco mais fundo —,
    // e so no fim o veu da tela (Agua.tsx) chega ao abismo da varredura. Sem
    // isto a agua clara escurecia para um cinza sujo a caminho do preto.
    const vec3 AZUL_MERGULHO = vec3(0.110, 0.227, 0.667);  // ~#1c3aaa

    float hash(vec2 p){
      return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
    }
    float ruido(vec2 p){
      vec2 i = floor(p), f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
                 mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
    }
    float fbm(vec2 p){
      float v = 0.0, a = 0.5;
      // Quatro oitavas, mas com queda rapida (0.42): a quarta devolve o
      // detalhe que da nitidez a superficie, e a amplitude baixa impede que
      // ela vire a granulacao de metal martelado.
      // Queda 0.50: as oitavas altas pesam mais e a superficie ganha detalhe.
      // Com 0.42 o campo saia macio demais e tudo parecia desfocado.
      for (int i = 0; i < 4; i++){ v += a * ruido(p); p *= 2.1; a *= 0.50; }
      return v / 0.9375;
    }

    // A agua virou funcao para poder ser amostrada em coordenada DESLOCADA —
    // e assim que a refracao do vidro funciona: ela le o fundo de outro ponto.
    vec3 agua(vec2 fragCoord){
      vec2 p = (fragCoord * 2.0 - uRes) / min(uRes.x, uRes.y);
      float ang = radians(50.0);                       // rotationZ
      p = mat2(cos(ang), -sin(ang), sin(ang), cos(ang)) * p;
      p.x *= cos(radians(10.0));                       // rotationY, achatamento
      p.x -= 0.27;                                     // positionX -0.9
      // Escala menor = formas MAIORES e mais espacadas. O 1.17 derivado do
      // uDensity 1.7 empacotava lamina sobre lamina; abrir o campo deixa cada
      // onda respirar, que e o que se ve em agua profunda.
      // No abismo o campo e amostrado numa escala MAIOR: formas menores, e
      // portanto mais ondas no mesmo quadro. A home fica em 0.80, que e a
      // abertura pensada para agua profunda vista de longe; a varredura pede
      // textura, nao uma onda so ocupando meia tela.
      p *= mix(0.80, 1.40, uFossa);

      // Mergulho: reduzir a area amostrada aproxima a camera do campo.
      // uMergulho ja vem de uma integracao de velocidade no JS, entao a
      // aceleracao esta na fisica e nao numa curva forcada aqui.
      float zoom = 1.0 + uMergulho * 5.5;
      p /= zoom;

      // O deslocamento temporal precisa encolher JUNTO com p. O tempo entra no
      // ruido como translacao (fbm(p + t)); se p encolhe 8x e t nao, o mesmo t
      // passa a valer 8x mais dentro do quadro e o campo dispara — era essa a
      // agitacao, nao a velocidade em si.
      //
      // Dividir pelo zoom mantem o fluxo APARENTE constante, que e o correto:
      // chegar perto da agua nao faz a agua se mexer mais rapido.
      // Duas leituras diferentes saem daqui, e elas nao se contradizem:
      //
      //   0.165 e o ritmo das DUAS telas — o movimento precisa se fazer
      //   notar, senao o campo parece uma imagem parada com um brilho preso
      //   nela. Medido, a tela passou a mudar 8.65 unidades de luminancia
      //   por segundo na home, contra 6.15 antes.
      //
      //   O freio de 20% no abismo e o que o mantem PESADO: ele corre a 80%
      //   da home, nao mais rapido. Ja esteve 2.35x mais rapido, para
      //   espalhar o brilho pela tela; isso deixou de ser necessario quando a
      //   luz virou crista e passou a nascer em todo canto do quadro.
      float t = uTime * 0.165 * (1.0 - uFossa * 0.20)
                * (1.0 - clamp(uMergulho, 0.0, 1.0) * 0.45) / zoom;
      // uStrength baixo de proposito: 3.9 enrola o campo em espirais, que e o
      // desenho do marmore e do mercurio. Agua deforma pouco e flui muito.
      float S = 1.55;

      vec2 q = vec2(fbm(p + vec2(0.0, t)), fbm(p + vec2(5.2, 1.3) - t * 0.8));
      vec2 r = vec2(fbm(p + S * q + vec2(1.7, 9.2) + 0.15 * t),
                    fbm(p + S * q + vec2(8.3, 2.8) - 0.13 * t));
      float f = fbm(p + S * r);

      // Componente ALONGADA: onda tem direcao, marmore nao. Comprimir um eixo
      // antes de amostrar estica as formas em laminas, e e o que separa a
      // leitura de agua da de metal derramado.
      float lamina = fbm(vec2(p.x * 0.34, p.y * 1.45) + S * 0.7 * r
                         + vec2(0.0, t * 0.55));

      // Uma unica transicao, nao o produto de duas: o produto criava pontos de
      // luz isolados e duros — reflexo especular de cromo. A faixa e mais
      // estreita que antes para dar recorte as laminas, mas continua sendo
      // uma so, entao o brilho ganha nitidez sem virar reflexo metalico.
      // MENOS AREA ACESA NA AGUA DE SEMPRE (2026-09-28): o pedido foi so o
      // reflexo esbranquicado da ondulacao, nada tao forte. A borda de cima
      // subiu de 0.72 para 0.76 na home — a luz cresce mais devagar, e a
      // massa inteira deixa de chegar ao topo ao mesmo tempo. O abismo da
      // varredura (uFossa = 1) fica como estava.
      float luz = smoothstep(mix(0.36, 0.14, uFossa),
                             mix(0.76, 0.70, uFossa),
                             f * 0.42 + lamina * 0.58);

      vec2 uv = fragCoord / uRes;
      float doCentro = distance(uv, vec2(0.5));

      // Medindo varios quadros, as laminas de luz passavam sob o titulo com
      // luminancia 210 — texto branco sumiria. Aqui elas sao suprimidas no
      // miolo e liberadas nas bordas, onde viram desenho em vez de ruido
      // sobre a leitura.
      // No mergulho a protecao cede: o hero ja se dissolveu, e deixar a luz
      // invadir o centro e o que da a sensacao de atravessar o campo.
      // Guarda o valor antes da protecao do centro. No modo escuro o texto e
      // branco e precisa que a luz recue no miolo; no modo claro o texto e
      // escuro, e recuar a luz ali abriria uma mancha azul atras da leitura —
      // exatamente o defeito oposto.
      float luzBruta = luz;

      // A faixa 0.16-0.60 cobria a tela inteira — numa proporcao 16:9 a borda
      // fica a ~0.57 do centro — e suprimia a luz a 12% em quase tudo. Agora e
      // rasa (45%) e acaba em 0.42. O texto continua com folga: medido, o
      // branco fica em 11:1 mesmo com crista passando por tras.
      float protecao = mix(0.45, 1.0, smoothstep(0.10, 0.42, doCentro));
      luz *= mix(protecao, 1.0, clamp(uMergulho * 3.0, 0.0, 1.0));

      // A protecao NAO pode ter forma reconhecivel. Duas tentativas antes
      // desta falharam pelo mesmo motivo: uma caixa arredondada, mesmo com a
      // borda desbastada por ruido, continua sendo um retangulo — renderizada
      // sozinha sobre campo aceso, aparece um bloco preto no meio da agua.
      //
      // Aqui a area vem de tres bolhas moles, uma por linha do conteudo:
      // titulo (duas linhas), barra com o termo, e o rodape de etapa e link.
      // A uniao delas nao tem eixo nem quina, e o campo de ruido desloca o
      // limite com forca (0.55 sobre uma distancia normalizada, ou seja mais
      // de meio raio), entao a fronteira serpenteia junto com o liquido em
      // vez de desenhar um contorno.
      float b1 = length((uv - vec2(0.500, 0.455)) / vec2(0.150, 0.085));
      float b2 = length((uv - vec2(0.500, 0.545)) / vec2(0.170, 0.060));
      float b3 = length((uv - vec2(0.500, 0.655)) / vec2(0.100, 0.075));
      float doTexto = min(min(b1, b2), b3) + (f - 0.5) * 0.55;
      // Queda longa (0.85 a 2.0 do raio): perto do texto a luz some, e ela
      // volta so ao dobro do raio de cada bolha. Corte curto le como recorte
      // por mais torta que seja a borda.
      // Piso em 0.30: o filamento AFINA sobre o texto, nao some. Quem garante a
      // leitura agora e o halo colado na letra (Varredura.module.css); aqui
      // basta tirar o pico de cima das palavras. Supressao total voltaria a
      // desenhar um vulto no meio da agua.
      float livre = mix(0.18, 1.0, smoothstep(0.85, 2.00, doTexto));

      // E aqui a protecao age sobre a PROPRIA luz, nao so sobre o termo
      // aditivo: com o brilho do abismo vindo de um mix suave, afinar luz
      // afina o reflexo inteiro na mesma proporcao. Foi o que nao servia
      // para a crista — ali multiplicar luz so deslocava onde a faixa caia.
      luz *= mix(1.0, mix(0.34, 1.0, livre), uFossa);

      // Aqui morava a CRISTA — uma faixa estreita em torno de um limiar, que
      // acendia so a quina das massas. Ela resolveu o problema da epoca (o
      // interior das massas saturava em branco e a tela virava um estencil),
      // mas trouxe o proprio: crista e uma LINHA por construcao. Nenhuma
      // largura a transforma em fumaca, e a tela escura ficou toda contornada
      // a giz enquanto a home continuava esfumacada.
      //
      // O abismo voltou ao sombreamento da home — o banho suave por luz, logo
      // abaixo —, e o preto passou a vir so de dois lugares: a base escurecida
      // e o filtro CSS. Fica a TURVACAO, que e o mesmo campo amaciado ao
      // quadrado: bruma, nao contorno.
      // TURVACAO: uma terceira faixa, muito mais larga e muito mais fraca,
      // em volta da mesma crista. Ela nao acrescenta filamento — suja o
      // entorno dele, como particula em suspensao pegando a luz. E o que
      // separa agua turva de vidro: no vidro a luz para na quina, na agua
      // turva ela vaza para os lados. Ao quadrado para concentrar perto do
      // fio e cair rapido, senao vira nevoa chapada e o preto vai junto.
      // A primeira turvacao mora em volta de 0.56; onde a luz esta longe
      // disso o quadro sai limpo. Esta segunda, no limiar da outra crista,
      // e o que enche essas regioes — sem ela a turvacao so acompanha um
      // filamento e a tela fica turva de um lado e vidro do outro.
      float turva2 = 1.0 - smoothstep(0.0, 0.18, abs(luz - 0.30));
      turva2 *= turva2;
      float turva = 1.0 - smoothstep(0.0, 0.32, abs(luz - 0.56));
      turva *= turva;

      // Tres camadas de profundidade: abismo no fundo, navy no meio-tom, e a
      // luz submersa nas laminas. O navy nunca cobre a tela inteira, e e isso
      // que faz o fundo parecer ter distancia.
      vec3 cor = mix(ABISSO, NAVY * 1.02, smoothstep(0.04, 0.64, f));
      // O meio-tom navy e o que impede o preto: ele ocupa a maior parte do
      // quadro, e nenhum filtro CSS o converte em preto sem levar os reflexos
      // junto. Apagar o meio-tom aqui, na origem, deixa o preto vir de graca.
      // 0.13, nao 0.18: o filtro caiu de contrast(2.9) para 2.0 para o
      // filamento parar de saturar em branco, e contraste menor levanta o
      // meio-tom junto. O preto perdido no filtro volta aqui, na origem,
      // sem reempurrar o brilho de volta ao branco chapado.
      // 0.50, nao 0.10. Este multiplicador apaga o campo navy — que e
      // justamente o que preenche a tela na home. Com ele em 0.10 o abismo
      // media 21% de presenca e caia a 12% no quadro mais vazio: a tela
      // ficava com um reflexo num canto e o resto no nada. E o vazio que
      // incomoda, nao a media, entao o numero que manda e o do pior quadro.
      cor *= mix(1.0, 0.18, uFossa);
      // Pela mesma razao o banho largo de AGUA recua quase por completo: ele
      // pinta AREA, e area azul clara e o oposto do abismo.
            // O banho de AGUA e a cor do CORPO do reflexo — e dele que vem o azul,
      // nao do pico nem do filtro. Recuar aqui e o que deixa o reflexo palido
      // em vez de azul; o saturate() do filtro so amplifica o que ja existe,
      // entao mexer nele sozinho nunca ia tirar a cor.
      // 0.52 na home (era 0.80): o corpo azul claro e o que pintava AREA; o
      // reflexo passa a vir do pico estreito logo abaixo. No abismo, 0.20
      // como antes.
      cor = mix(cor, AGUA, luz * (0.52 - uFossa * 0.32));
      // E o topo da faixa — o que enchia o interior das massas — se apaga.
      // O pico e LUZ tambem no abismo. Ja esteve azul, para o reflexo nao
      // lavar no branco justo onde e mais forte; so que com o campo cheio a
      // tela inteira virou azul, e ai foi a leitura que passou do ponto. O
      // branco no topo e o que da a sensacao de luz atravessando — a cor da
      // agua vem do banho de AGUA logo acima, nao do pico.
      cor = mix(cor, LUZ, smoothstep(0.78, 0.99, luz) * (0.05 + uFossa * 0.03));

      // Especular. Duas escolhas o mantem como agua e nao como cromo:
      //   1. ADITIVO, nao mix — luz soma sobre a superficie; substituir a cor
      //      da agua por branco e o que produzia reflexo de metal polido.
      //   2. pico ESTREITO — a potencia 5 comprime o smoothstep num topo fino,
      //      entao o brilho aparece so na quina das laminas, como glint na
      //      face de uma onda, em vez de manchar a crista inteira.
      // A faixa termina em 0.92, nao em 1.0: o campo de luz quase nunca satura,
      // e mirar em 1.0 deixava o especular invisivel (0,02% da tela).
      // ATENCAO: nada de crase neste bloco — o shader vive dentro de um
      // template literal, e uma crase perdida encerra a string e derruba todo
      // o JavaScript da pagina em silencio.
      // O PICO FICOU ESTREITO na home (0.64 a 0.93, potencia 2.6; era 0.52 a
      // 0.76, potencia 1.8). Com a faixa larga o especular virava um PLATO:
      // todo o interior da massa acesa somava branco, e em certos momentos um
      // quinto da tela saia branca. Medido numa copia do shader em Python, 40
      // quadros espalhados no tempo: tela quase branca (luminancia acima de
      // 200) caiu de 5.4% para 0.6% em media e de 17.8% para 2.9% no pior
      // momento; o reflexo mais forte de cada quadro continua esbranquicado
      // (192 tipico, era 245). No abismo, a faixa e a potencia de antes.
      // A AGUA EM MOVIMENTO MAIS RELUZENTE (2026-09-29, pedido do Pedro, so
      // na home): o reflexo acende um pouco antes (0.58, era 0.64) e mais
      // forte (1.15, era 0.85), logo abaixo. Nada mais mudou. Medido na copia
      // em Python: o branco (luminancia acima de 200) sobe de 0.6% para 1.2%
      // da tela em media, e de 2.9% para 5.7% no pior quadro.
      float glint = pow(smoothstep(mix(0.58, 0.52, uFossa),
                                   mix(0.93, 0.76, uFossa), luz),
                        mix(2.6, 1.8, uFossa));
      // No abismo o pico largo recua e as cristas assumem: aditivo e sem
      // teto, porque com o fundo em preto de verdade o unico jeito de ficar
      // mais brilhante e o filamento estourar o branco. Saturar e o objetivo
      // aqui, nao o defeito.
      // EQUILIBRIO LATERAL (2026-09-29, pedido do Pedro: "do lado direito
      // esta muito forte, pode diminuir so um pouco, mas quero que fique mais
      // presente no lado esquerdo"): na home o reflexo vai de 1.12x na borda
      // esquerda a 0.78x na direita.
      // Na home o glint e REFLEXO (lavanda) a 1.10, nao LUZ branca a 1.15:
      // o nucleo tipico do reflexo cai de 204 para 164 de luminancia (o
      // recorte de referencia do Pedro tem 172), no mesmo lugar e na mesma
      // frequencia de antes.
      cor += mix(REFLEXO, LUZ, uFossa) * glint * mix(1.10, 0.20, uFossa)
             * mix(mix(1.12, 0.78, uv.x), 1.0, uFossa);
      // O texto da varredura vive numa faixa central — titulo, termo, barra
      // de progresso, fase e detalhe. Filamento passando por tras dele nao e
      // desenho, e ruido sobre a leitura.
      //
      // A protecao que ja existia (protecao, acima) nao resolve isto: ela
      // MULTIPLICA luz, e a crista nao e "acima de um limiar", e "perto de um
      // limiar" — abaixar luz nao apaga a crista, so desloca onde ela cai. Na
      // pratica ela suprimia a primeira crista (0.56 exigiria luz original
      // 1.24, impossivel) e deixava a segunda (0.30 exige 0.67, comum) passar
      // por cima do titulo. Por isso a supressao aqui e explicita.
      //
      // A forma acompanha o BLOCO DE TEXTO, nao um circulo em volta dele: uma
      // caixa arredondada com as medidas dos elementos reais — a barra de
      // progresso e o mais largo (x 0.35 a 0.65), o titulo e a linha do link
      // dao a altura (y 0.40 a 0.70). A elipse anterior apagava de 0.12 a
      // 0.88 da largura para proteger um bloco que ocupa um terco disso.
      // Os dois termos NAO sao protegidos do mesmo jeito, e e isso que faz a
      // protecao sumir de vista:
      //
      //   - o FILAMENTO e fino e chega perto do branco. E ele que cruza uma
      //     letra e come a leitura, entao ele desvia de verdade.
      //   - a TURVACAO e larga e fraca. Ela nao atrapalha ler; mas era ela
      //     que, ao ser cortada, abria um vulto escuro no meio da agua —
      //     recortar uma nevoa larga sempre deixa a forma do recorte a
      //     mostra. Entao ela so afina sobre o texto, nunca some.
      //
      // Buraco tem silhueta; regiao onde a luz afina nao tem.
      // So a bruma sobra do termo aditivo, e ela quase nao cede sobre o
      // texto: recortar nevoa larga deixa a forma do recorte a mostra. Quem
      // protege a leitura e o afinamento de luz la em cima, que age sobre o
      // reflexo inteiro, mais o halo colado na letra (Varredura.module.css).
      cor += FIO * uFossa * (turva * 0.30 + turva2 * 0.15)
                          * mix(0.85, 1.0, livre);

      // Escurece as bordas: da profundidade ao campo. A vinheta tambem abre
      // durante o mergulho, senao a tela pareceria afundar num tunel escuro.
      float vinheta = smoothstep(1.25, 0.20, doCentro);
      float abertura = clamp(uMergulho * 2.2, 0.0, 1.0);
      // A vinheta quase se abre no abismo: ela cortava as bordas a 34% e era
      // metade da razao de a luz sobreviver so num anel no meio da tela.
      cor *= mix(mix(mix(0.34, 0.88, uFossa), 1.0, vinheta),
                 1.0, abertura * 0.75);

      // REFLEXOS QUE NASCEM EM OUTROS PONTOS (2026-09-29, so na home). A agua
      // tem uma ou duas massas de luz por vez, e elas mudam de lugar em
      // minutos: um lado podia passar minutos sem reflexo nenhum. Esta camada
      // e o MESMO tipo de campo (nebuloso, deformado pelo mesmo r), menor e
      // deslocado, entao acende em outros pontos; entra DEPOIS da vinheta, que
      // escurece o azul da borda sem apagar o reflexo; e nasce mais do lado
      // esquerdo (1.30x la, 0.45x na borda direita), onde faltava. Medido na
      // copia em Python, 10 minutos depois de abrir a pagina: a esquerda com
      // reflexo em 70% do tempo (era 39%), no maximo 1.3 min seguido sem
      // nenhum (era 1.8); a direita um pouco mais fraca (area clara de 3.6%
      // para 3.2%).
      float branco = fbm(p * 2.0 + S * 0.9 * r + vec2(11.0 + t * 0.09, 4.0 - t * 0.0675));
      float reflexo = pow(smoothstep(0.55, 0.84, branco), 1.4)
                      * protecao * mix(0.82, 1.0, vinheta) * mix(1.30, 0.45, uv.x);
      cor += REFLEXO * reflexo * 0.86 * (1.0 - uFossa);

      // Modo claro: agua rasa e ensolarada, vista de cima. NAO e o negativo do
      // abismo — a relacao de valor e a mesma (luz clareia, sombra escurece),
      // com a faixa tonal inteira deslocada para cima. Inverter, fazendo a luz
      // virar azul escuro, produz um negativo fotografico.
      //
      // REFEITA EM 19/10 ("as ondulacoes e efeitos nao ficaram tao bons como
      // no dark"). A primeira versao so levava a base ao branco pela luz, e sem
      // a camada que da relevo a agua lia como nevoa chapada. Agora ela tem as
      // MESMAS camadas do escuro, uma por uma:
      //   1. o CORPO — o vale e a crista pelo campo f, no papel do abismo e do
      //      navy;
      //   2. a DOBRA — a faixa logo antes de cada lamina acender escurece para
      //      um azul medio. E a sombra da onda, e e ela que desenha a
      //      ondulacao: sem ela a lamina branca some no fundo claro;
      //   3. a LAMINA — clareia ate o branco, como a luz no escuro;
      //   4. o BRILHO — o glint e os reflexos do escuro, em branco, por cima.
      // Os campos (f, luzBruta, glint, reflexo) sao os do escuro, sem nenhuma
      // conta nova: o movimento e o desenho sao os mesmos, so a cor muda.
      const vec3 C_VALE  = vec3(0.733, 0.792, 0.953);  // #bbcaf3
      const vec3 C_DOBRA = vec3(0.475, 0.580, 0.875);  // #7994df
      const vec3 C_BASE  = vec3(0.918, 0.941, 0.996);  // #eaf0fe
      const vec3 C_LUZ   = vec3(1.000, 1.000, 1.000);

      vec3 claro = mix(C_VALE, C_BASE, smoothstep(0.10, 0.70, f));
      float dobra = smoothstep(0.10, 0.40, luzBruta)
                    * (1.0 - smoothstep(0.40, 0.72, luzBruta));
      claro = mix(claro, C_DOBRA, dobra * 0.62);
      claro = mix(claro, C_LUZ, smoothstep(0.55, 0.95, luzBruta) * 0.88);
      claro = mix(claro, C_LUZ, clamp(glint * 1.10 + reflexo * 0.85, 0.0, 1.0));

      // Vinheta suave e AZULADA: da forma sem furar o branco. Escurecer para
      // preto aqui sujaria a tela; puxar para o vale da agua mantem a cena.
      float v2 = smoothstep(1.35, 0.30, doCentro);
      claro = mix(mix(claro, C_VALE, 0.30), claro, v2);

      // A LEITURA NO CLARO. O titulo e o texto ficam sobre o miolo da tela, e a
      // dobra azul passando por tras deles baixava o contraste. O miolo puxa
      // para a base clara — uma queda radial LARGA e macia, de 0.08 a 0.50 do
      // centro, que se le como a luz batendo no meio da cena, e nao como um
      // recorte. A ondulacao continua desenhada ali, so mais rasa; a borda
      // fica com a agua inteira.
      claro = mix(mix(claro, C_BASE, 0.62), claro, smoothstep(0.08, 0.50, doCentro));

      cor = mix(cor, claro, clamp(uClaro, 0.0, 1.0));

      // A luz cai com a profundidade ao longo de TODA a descida, nao so no
      // corte final: e assim que o mar escurece conforme se afunda, e e o que
      // da peso ao movimento.
      // No claro a mesma descida AZULA em vez de escurecer: multiplicar a agua
      // clara por 0.5 dava um cinza. Com uClaro = 0 a conta e a de sempre.
      float fundoDaDescida = clamp(uMergulho * 0.42, 0.0, 0.5);
      cor = mix(cor * (1.0 - fundoDaDescida),
                mix(cor, AZUL_MERGULHO, fundoDaDescida * 1.2),
                clamp(uClaro, 0.0, 1.0));

      // E entao se apaga por completo antes da troca de tela, para nao existir
      // um quadro em que as duas telas aparecam.
      // No escuro, mix com preto e o mesmo que cor *= 1 - uEscuro, como sempre;
      // no claro o fim da descida e o azul do mergulho.
      cor = mix(cor, AZUL_MERGULHO * clamp(uClaro, 0.0, 1.0), uEscuro);
      return cor;
    }

    void main(){
      gl_FragColor = vec4(agua(gl_FragCoord.xy), 1.0);
    }
`;
